import { useIsPreviewOrDemo } from "../../../contexts/IsPreviewOrDemoContext";
import {
  Configuration,
  RegistryDetails,
  Section,
  SectionTypes,
} from "../../../resources/modelTypes";
import SitePreviewContext from "../../../website/components/SitePreviewContext";
import { themeTypographyResetStyles } from "../../../website/components/themeTypographyResetStyles";
import { DisplayPhotoOrVideo } from "../DisplayPhotoOrVideo";
import {
  getEditStateBorderStyle,
  getSectionEditState,
  SectionEditState,
} from "../DisplayWebsiteContent";
import { useWebsiteWidth } from "../WebsiteWidthContext";
import CashRegistryDefaultImage from "../assets/cashRegistryDefaultSmall.webp";
import RegistryDefaultImage from "../assets/registryDefaultSmall.webp";
import PostGivingMessageModal from "./PostGivingMessageModal";
import { RegistryDetailModal } from "./RegistryDetailModal";
import { css } from "@emotion/react";
import {
  PillButton,
  styleUtils,
  theme,
  useMediaQueryState,
  usePointerState,
} from "@minted/minted-components";
import React, { memo, useCallback, useContext, useState } from "react";

const cardStyles = {
  card: (isMobile: boolean) => css`
    background-color: ${theme.colors.gray100};
    border: ${styleUtils.rem(1)} solid ${theme.colors.border};
    border-radius: ${styleUtils.rem(8)};
    color: ${theme.colors.textPrimary};
    ${isMobile && `flex: 0 0 calc(50% - ${theme.spacing.x2});`}
    min-height: ${isMobile ? styleUtils.rem(208) : styleUtils.rem(332)};
    width: ${isMobile ? "100%" : styleUtils.rem(256)};
  `,
  imageWrapper: css`
    aspect-ratio: 1;
    cursor: pointer;
    overflow: hidden;
    width: 100%;
    border-top-left-radius: ${styleUtils.rem(8)};
    border-top-right-radius: ${styleUtils.rem(8)};
  `,
  imageInner: css`
    height: 100%;
    width: 100%;

    & img {
      height: 100%;
      object-fit: cover;
      width: 100%;
    }
  `,
  noImagePlaceholder: css`
    align-items: center;
    background-color: ${theme.colors.white};
    display: flex;
    height: 100%;
    justify-content: center;
    width: 100%;
  `,
  noImageTitle: css`
    ${theme.typeStyles.displayExtraExtraSmallStyles};
    font-style: italic;
    text-align: center;
  `,
  pillButton: css`
    display: flex;
    justify-content: center;
    margin-bottom: ${theme.spacing.x2};
  `,
  textBlock: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.base};
    padding: ${theme.spacing.x4};
    text-align: left;
    ${theme.media.lessThan(theme.breakpoints.small)(`
      padding: ${theme.spacing.x2};
      padding-bottom: ${theme.spacing.x3};
    `)};
  `,
  subtitle: css`
    ${theme.typeStyles.bodyExtraSmallStyles};
    display: flex;
    color: ${theme.colors.textSecondary};
  `,
  title: css`
    display: flex;
    overflow-wrap: break-word;
    word-break: break-word;
    ${theme.typeStyles.titleSmallStyles};
  `,
};

export type RegistryCardProps = {
  configuration: Configuration;
  details: RegistryDetails;
  isPreviewClickable?: boolean;
  section: Section;
  subtitle?: string;
};

const RegistryCard = ({
  configuration,
  details,
  isPreviewClickable,
  section,
  subtitle: subtitleProp,
}: RegistryCardProps) => {
  const { isDemo } = useIsPreviewOrDemo();
  const width = useWebsiteWidth();
  const isMobile: boolean = width !== null && width < 600;

  // Card layout keys off the preview container width above, but the reveal stage
  // must key off the pointer and the real viewport — a desktop user who has
  // narrowed the preview to the mobile breakpoint still has hover.
  //
  // Touch devices additionally cannot carry the hover handlers.
  const smallMediaQueryState = useMediaQueryState({
    mediaQuerySize: "small",
  });
  const isTapToReveal =
    usePointerState() === "TOUCH" || smallMediaQueryState === "BELOW";

  const {
    focusedClickSection,
    hoveredSection,
    setFocusedClickSection,
    setHoveredSection,
    onSectionEditClickWithScroll,
  } = useContext(SitePreviewContext);

  const [isRegistryDetailModalOpen, setIsRegistryDetailModalOpen] =
    useState(false);
  const [isPostGivingMessageModalOpen, setIsPostGivingMessageModalOpen] =
    useState(false);

  const subtitle =
    subtitleProp ??
    (section.sectionType === SectionTypes.CashRegistry
      ? "Cash fund"
      : "Registry");

  const isSectionFocused =
    focusedClickSection?.id === section.id &&
    focusedClickSection?.type === "section";

  const isSectionHovered =
    hoveredSection?.id === section.id && hoveredSection?.type === "section";

  const isClickable = Boolean(isPreviewClickable);

  const shouldTrackHover = isClickable && !isTapToReveal;

  const cardEditState = getSectionEditState({
    isClickable,
    isFocused: isSectionFocused,
    isHovered: isSectionHovered,
    isSolidBorder: Boolean(focusedClickSection?.isSolidBorder),
  });

  const cardBorderStyle = getEditStateBorderStyle(cardEditState);

  const shouldDisplayPillButton =
    isClickable && cardEditState === SectionEditState.Revealed;

  /**
   * Reveals the card on the first click and opens its editor on the second.
   * Desktop skips the reveal because hover already shows the border and pill.
   */
  const handleEditClick = useCallback(() => {
    if (cardEditState === SectionEditState.Hidden && isTapToReveal) {
      setFocusedClickSection({
        id: section.id,
        isSolidBorder: false,
        type: "section",
      });

      return;
    }

    setFocusedClickSection({
      id: section.id,
      isSolidBorder: true,
      type: "section",
    });
    onSectionEditClickWithScroll(section.id, "section", section.sectionType);
  }, [
    cardEditState,
    isTapToReveal,
    section.id,
    section.sectionType,
    setFocusedClickSection,
    onSectionEditClickWithScroll,
  ]);

  const openModal = useCallback(() => {
    if (isPreviewClickable) {
      handleEditClick();
      return;
    }
    if (isDemo) {
      return;
    }
    setIsRegistryDetailModalOpen(true);
  }, [isPreviewClickable, handleEditClick, isDemo]);

  const closeModal = useCallback(() => {
    setIsRegistryDetailModalOpen(false);
  }, []);

  const openPostGivingMessageModal = useCallback(() => {
    closeModal();
    setIsPostGivingMessageModalOpen(true);
  }, [closeModal]);

  const handleContributeClick = useCallback(() => {
    openPostGivingMessageModal();
  }, [openPostGivingMessageModal]);

  const closePostGivingMessageModal = useCallback(() => {
    setIsPostGivingMessageModalOpen(false);
  }, []);

  const showNoImagePlaceholder =
    details.hideDefaultRegistryImage && !section.image && !section.photo;

  const content = (
    <>
      <div
        css={cardStyles.imageWrapper}
        onClick={openModal}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openModal();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div css={cardStyles.imageInner}>
          {showNoImagePlaceholder ? (
            <div css={cardStyles.noImagePlaceholder}>
              <span css={cardStyles.noImageTitle}>{details.title}</span>
            </div>
          ) : (
            <DisplayPhotoOrVideo
              configuration={configuration}
              isRegistryPhoto
              section={section}
              defaultImage={
                section.sectionType === SectionTypes.Registry
                  ? RegistryDefaultImage
                  : CashRegistryDefaultImage
              }
            />
          )}
        </div>
      </div>

      <div
        css={cardStyles.textBlock}
        onClick={openModal}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openModal();
          }
        }}
        role="button"
        tabIndex={0}
      >
        {subtitle && <div css={cardStyles.subtitle}>{subtitle}</div>}
        {details.title && <div css={cardStyles.title}>{details.title}</div>}
      </div>
    </>
  );

  return (
    <>
      <div
        onMouseEnter={
          shouldTrackHover
            ? () => setHoveredSection({ id: section.id, type: "section" })
            : undefined
        }
        onMouseLeave={
          shouldTrackHover ? () => setHoveredSection(null) : undefined
        }
      >
        {shouldDisplayPillButton && (
          <div css={cardStyles.pillButton}>
            <PillButton
              iconAlignment="right"
              iconType="editSquare"
              onClick={handleEditClick}
              size="small"
            />
          </div>
        )}
        <div
          css={[
            cardStyles.card(isMobile),
            cardBorderStyle,
            themeTypographyResetStyles,
          ]}
        >
          {content}
        </div>
      </div>

      {isRegistryDetailModalOpen && (
        <RegistryDetailModal
          configuration={configuration}
          details={details}
          onClose={closeModal}
          open={isRegistryDetailModalOpen}
          section={section}
          onContributeClick={handleContributeClick}
        />
      )}

      {isPostGivingMessageModalOpen && (
        <PostGivingMessageModal
          open={isPostGivingMessageModalOpen}
          onSkipClick={closePostGivingMessageModal}
        />
      )}
    </>
  );
};

const MemoizedRegistryCard = memo(RegistryCard);

export { MemoizedRegistryCard as RegistryCard };
